import React, { useState } from 'react';

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
    <path d="M2 5l2 2 4-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" /><path d="M12 8h.01" />
  </svg>
);

const ATS_GROUPS = {
  basic:      { label: 'Basic',      list: ['Keka', 'PyjamaHR', 'Recruiterbox', 'Workable', 'Zoho Recruit'] },
  premium:    { label: 'Premium',    list: ['Lever', 'Greenhouse', 'Ashby'] },
  enterprise: { label: 'Enterprise', list: ['Workday', 'SmartRecruiters', 'Darwinbox', 'Phenom', 'Others'] },
} as const;

type AtsKey = keyof typeof ATS_GROUPS;

interface SupportChannel {
  emoji: string;
  type: string;
  desc: string;
}

interface Tier {
  accent: string;
  accentLight: string;
  accentBorder: string;
  accentText: string;
  ctaAccent: string;
  tagline: string;
  sub: string;
  support: SupportChannel[];
  ats: AtsKey[];
  billing: 'monthly' | 'both' | 'enterprise';
  price: { annual?: string; monthly?: string };
}

const TIERS: Tier[] = [
  {
    accent: '#5851D3', accentLight: '#EEEDFE', accentBorder: '#CCC9F5', accentText: '#3B3499', ctaAccent: '#5851D3',
    tagline: 'Hire your first tech team without the overhead',
    sub: 'Simple, monthly plan with essential tools to get your earliest hires done.',
    support: [{ emoji: '✉️', type: 'Email', desc: 'Responses within 1 business day' }],
    ats: ['basic'],
    billing: 'monthly',
    price: { monthly: '₹9,999/mo' },
  },
  {
    accent: '#2563EB', accentLight: '#EFF6FF', accentBorder: '#BFDBFE', accentText: '#1E40AF', ctaAccent: '#2563EB',
    tagline: 'Scale your team without friction',
    sub: 'Chat support and all major ATSs so your recruiters can work inside the tools they already use.',
    support: [{ emoji: '💬', type: 'Chat', desc: 'Real-time help during business hours' }],
    ats: ['basic', 'premium'],
    billing: 'both',
    price: { annual: '₹1,99,970/yr', monthly: '₹20,000/mo' },
  },
  {
    accent: '#059669', accentLight: '#ECFDF5', accentBorder: '#6EE7B7', accentText: '#065F46', ctaAccent: '#0A5C47',
    tagline: 'Structured hiring for fast-growing orgs',
    sub: 'Built for orgs running multiple hiring pipelines simultaneously, with full recruiter tooling.',
    support: [
      { emoji: '💬', type: 'Chat', desc: 'Real-time help during business hours' },
      { emoji: '📞', type: 'Phone', desc: 'Scheduled calls with your success team' },
    ],
    ats: ['basic', 'premium'],
    billing: 'both',
    price: { annual: '₹2,99,999/yr', monthly: '₹29,999/mo' },
  },
  {
    accent: '#EFBF04', accentLight: '#FFFBEB', accentBorder: '#FDE68A', accentText: '#92400E', ctaAccent: '#EFBF04',
    tagline: 'Hire at scale with your existing workflows',
    sub: 'Full enterprise ATS coverage, priority support, and custom integrations — no workflow disruption.',
    support: [
      { emoji: '💬', type: 'WhatsApp', desc: 'Priority response, anytime' },
      { emoji: '📞', type: 'Phone', desc: 'Dedicated account manager + SLA' },
    ],
    ats: ['basic', 'premium', 'enterprise'],
    billing: 'enterprise',
    price: {},
  },
];

const TABS = [
  { size: '1–25',    label: 'Early-stage' },
  { size: '26–100',  label: 'Startup' },
  { size: '101–500', label: 'Growth' },
  { size: '500+',    label: 'Enterprise' },
];

const ALWAYS_INCLUDED = [
  'Unlimited job postings',
  'Unlimited resume views and downloads (fair usage)',
  'No seat limits',
  'No salary caps',
];

const Pill: React.FC<{ accent: string; accentText: string; title: string; children: React.ReactNode }> = ({ accent, accentText, children, title }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 20,
        background: '#fff', border: `1px solid ${accent}`, color: accentText,
        cursor: 'default', transition: 'opacity 0.15s', opacity: hover ? 0.75 : 1,
      }}
    >
      {children}
      <span style={{ display: 'flex', alignItems: 'center', opacity: 0.6 }}><InfoIcon /></span>
    </span>
  );
};

const SubscriptionModel = () => {
  const [idx, setIdx] = useState(1);
  const [annual, setAnnual] = useState(true);
  const tier = TIERS[idx];
  const isEnterprise = tier.billing === 'enterprise';
  const isMonthlyOnly = tier.billing === 'monthly';
  const hasBillingToggle = tier.billing === 'both';

  const ctaLabel = isEnterprise ? 'Get a callback' : 'Start free trial';
  const ctaHref = isEnterprise
    ? 'https://share.hsforms.com/1y0l7U0OHRo2JLsKVJLBhnAbpcum'
    : 'https://cutshort.io/?auth=employer';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 20px 20px', fontFamily: 'inherit' }}>

      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h2 style={{ fontSize: 42, fontWeight: 800, margin: 0, color: '#111' }}>Freedom</h2>
        <span style={{
          fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '4px 12px',
          borderRadius: 20, background: '#f97316', color: '#fff', letterSpacing: '0.8px',
        }}>2026 launch</span>
      </div>
      <p style={{ fontSize: 15, color: '#555', margin: '0 0 24px', lineHeight: 1.6, textAlign: 'center', maxWidth: 460 }}>
        No usage limits. No seat limits. One flat pricing based on your company size.
      </p>

      {/* Card */}
      <div style={{
        maxWidth: 460, width: '100%', borderRadius: 20, background: '#fff',
        border: `2px solid ${tier.accentBorder}`, overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)', transition: 'border-color 0.3s ease',
      }}>

        {/* Size tabs */}
        <div style={{ padding: '20px 20px 0' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', textAlign: 'center', margin: '0 0 10px' }}>
            What&apos;s your company size?
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#f3f4f6', borderRadius: 12, padding: 4, gap: 3 }}>
            {TABS.map((tab, i) => (
              <button
                key={i}
                onClick={() => { setIdx(i); setAnnual(TIERS[i].billing !== 'monthly'); }}
                style={{
                  padding: '8px 4px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  textAlign: 'center', fontFamily: 'inherit', transition: 'all 0.2s',
                  background: i === idx ? TIERS[i].accent : 'transparent',
                  boxShadow: i === idx ? `0 2px 8px ${TIERS[i].accent}40` : 'none',
                }}
              >
                <span style={{ display: 'block', fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: i === idx ? '#fff' : '#374151' }}>{tab.size}</span>
                <span style={{ display: 'block', fontSize: 10, marginTop: 1, color: i === idx ? 'rgba(255,255,255,0.85)' : '#6b7280' }}>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>

          {/* Tagline */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>{tier.tagline}</div>
            <div style={{ fontSize: 13, color: '#555', marginTop: 4, lineHeight: 1.5 }}>{tier.sub}</div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />

          {/* Always included */}
          <p style={{ fontSize: 11, fontWeight: 700, color: tier.accentText, letterSpacing: '0.5px', margin: '0 0 10px' }}>Always included</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ALWAYS_INCLUDED.map(f => (
              <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ width: 20, height: 20, minWidth: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tier.accentText }}>
                  <CheckIcon />
                </span>
                <span style={{ fontSize: 13, color: '#111', lineHeight: 1.5 }}>{f}</span>
              </li>
            ))}
          </ul>

          <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />

          {/* Support */}
          <p style={{ fontSize: 11, fontWeight: 700, color: tier.accentText, letterSpacing: '0.5px', margin: '0 0 10px' }}>Support</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tier.support.map(ch => (
              <Pill key={ch.type} accent={tier.accent} accentText={tier.accentText} title={ch.desc}>
                <span style={{ fontSize: 14 }}>{ch.emoji}</span>
                {ch.type}
              </Pill>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />

          {/* ATS */}
          <p style={{ fontSize: 11, fontWeight: 700, color: tier.accentText, letterSpacing: '0.5px', margin: '0 0 10px' }}>ATS integrations</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tier.ats.map(key => (
              <Pill key={key} accent={tier.accent} accentText={tier.accentText} title={ATS_GROUPS[key].list.join(', ')}>
                {ATS_GROUPS[key].list.length} {ATS_GROUPS[key].label} ATS
              </Pill>
            ))}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />

          {/* Enterprise custom text */}
          {isEnterprise && (
            <p style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '12px 0 0' }}>Get your own freedom plan</p>
          )}

          {/* Monthly-only price */}
          {isMonthlyOnly && (
            <>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7, padding: '10px 12px', borderRadius: 12, border: `1.5px solid ${tier.accent}`, background: '#fff' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', border: `4px solid ${tier.accent}`, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: tier.accentText }}>Monthly</span>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: tier.accentText }}>{tier.price.monthly}</span>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#888', margin: '12px 0 0', padding: '8px 12px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                Monthly billing only · Card payments
              </div>
            </>
          )}

          {/* Annual/monthly toggle */}
          {hasBillingToggle && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {[
                { label: 'Annual', value: true, price: tier.price.annual },
                { label: 'Monthly', value: false, price: tier.price.monthly },
              ].map(opt => (
                <div
                  key={opt.label}
                  onClick={() => setAnnual(opt.value)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 7, padding: '10px 12px',
                    borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s',
                    border: `1.5px solid ${annual === opt.value ? tier.accent : '#e5e7eb'}`,
                    background: annual === opt.value ? '#fff' : '#f9fafb',
                  }}
                >
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                    border: `${annual === opt.value ? 4 : 2}px solid ${annual === opt.value ? tier.accent : '#9ca3af'}`,
                    background: '#fff',
                  }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: annual === opt.value ? tier.accentText : '#374151' }}>{opt.label}</span>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: annual === opt.value ? 800 : 400, color: annual === opt.value ? tier.accentText : '#6b7280', marginTop: 1 }}>{opt.price}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div style={{ marginTop: 16 }}>
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', width: '100%', padding: 14, fontSize: 15, fontWeight: 700, borderRadius: 12,
                background: tier.ctaAccent, border: `1px solid ${tier.ctaAccent}`, color: '#fff',
                cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', textDecoration: 'none',
                transition: 'opacity 0.15s', boxSizing: 'border-box',
              }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              {ctaLabel}
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SubscriptionModel;
